import { computed, ref, watch } from 'vue';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CalendarConfig } from './types';

export interface CalendarSectionProps {
    imageElement?: ImagePlaceholderElement;
}

export type CalendarConfigEmit = (event: 'update-config', config: Partial<CalendarConfig>) => void;

/**
 * Keeps a local copy of the element config; persisting, normalizing and re-rendering is done by the
 * step one controller through the emitted `update-config` event.
 */
export const useCalendarConfig = (props: CalendarSectionProps, emit: CalendarConfigEmit) => {
    const config = ref<CalendarConfig>();
    // Remounts the section on element change so local UI state (e.g. stroke toggles) is rebuilt
    const elementKey = ref(0);

    watch(
        () => props.imageElement,
        (element) => {
            config.value = element?.calendarConfig ? { ...element.calendarConfig } : undefined;
            elementKey.value++;
        },
        { immediate: true }
    );

    const disabled = computed(() => !!props.imageElement?.locked);

    const update = (partial: Partial<CalendarConfig>) => {
        if (!config.value) return;
        config.value = { ...config.value, ...partial };
        emit('update-config', partial);
    };

    return { config, elementKey, disabled, update };
};
